/**
 * SENSE (vision + hearing) shared model, change thresholds and direction logic.
 *
 * ONE place for: the sense-2.0 record shape, the "did it change" thresholds, the
 * status/value extraction (the iron rule that a non-ok metric is NEVER a number),
 * and the better() comparison whose signs are INVERTED between the two metrics.
 * Imported by both the consumer results card and the provider (doctor) report so
 * the two surfaces can never disagree about a direction. (Handoff 02 sec D, 05.)
 */

export type SenseMetric = 'vision' | 'hearing';
/**
 * Per-module measurement status (02_VALIDITY sec A). 'ceiling'/'floor' are the two
 * edge states the 2026-09 measurement package added: the staircase ran to the easy
 * (ceiling) or hard (floor) end without converging. Both are NON-ok, so metricValue
 * returns null for them — they are "not measured", never a number.
 */
export type SenseStatus =
  | 'ok'
  | 'inconclusive'
  | 'ceiling'
  | 'floor'
  | 'skipped'
  | 'missing';

/** The two-block SENSE record persisted verbatim on `scan.sense` by assets/sense-app. */
export interface SenseRecord {
  module?: string;
  engine_version?: string;
  taken_at?: string;
  vision_contrast?: {
    status?: string;
    log_cs?: number | null;
    code?: number;
    level_index?: number;
    levels_total?: number;
    presented?: number;
    warmup_fails?: number;
  } | null;
  hearing_tone?: {
    status?: string;
    snr_db?: number | null;
    trials?: number;
    reversals?: number;
    reversals_used?: number;
    /** which range edge the staircase bottomed out on: 'none' | 'easy' | 'hard'. */
    terminated_at_edge?: string;
    /** e.g. 'not_audible' when the warm-up audibility gate failed. */
    reason?: string | null;
  } | null;
  /** metrics that produced a valid (ok) value / that did not (02_VALIDITY sec ג). */
  modules_used?: string[];
  modules_missing?: string[];
  context?: unknown;
}

/**
 * Engine versions of the current two-block SENSE model (vision + hearing). The
 * 2026-09 measurement package bumped the staircase params, so it is a new engine
 * (02_VALIDITY sec ד). Both are STRUCTURALLY the current model (shown, not archived),
 * but points from two different engines are a different baseline and are never
 * plotted on one trend line — see `senseEngineOf` + the buildSenseVM filter.
 */
export const SENSE_ENGINES_2X: readonly string[] = ['sense-2.0', 'sense-2.1'];

/**
 * Change thresholds — the SINGLE place they live (handoff 02 sec D).
 * VISION: the scale is discrete, ~0.31 logCS between adjacent levels, so a move
 * smaller than one level is measurement noise, not a change.
 * HEARING: 4.0 dB is a PLACEHOLDER, not a measured value. It must be validated by
 * running ~20 women twice in one session and taking ~1.5x the SD of the diff.
 * Until then it stays here in config and is not scattered through the code.
 */
export const SENSE_MIN_DELTA: Record<SenseMetric, number> = {
  vision: 0.3, // logCS, one level
  hearing: 4.0, // dB, placeholder pending validation
};

/**
 * "Better" comparison. 🔴 The signs are INVERTED between the two metrics, which is
 * the classic trap the handoff calls out: a HIGHER logCS means she sees fainter
 * contrast (better), while a LOWER snr_db means she hears the tone in relatively
 * louder noise (better). One function, never two separate if-branches.
 */
export const SENSE_BETTER: Record<SenseMetric, (a: number, b: number) => boolean> = {
  vision: (a, b) => a > b, // higher logCS is better
  hearing: (a, b) => a < b, // lower (more negative) SNR is better
};

/** A record is part of the current (two-block) series when its engine is 2.x. */
export function isSense2(rec: unknown): rec is SenseRecord {
  return (
    !!rec &&
    typeof rec === 'object' &&
    SENSE_ENGINES_2X.includes((rec as SenseRecord).engine_version || '')
  );
}

/**
 * A sense record that exists but is NOT a current-model (2.x) record — the old
 * 6-subtest series. It is archived: never graphed, never shown to the user, never in
 * the doctor report. (Handoff 02 sec Z.) Old records carry no 2.x engine_version.
 */
export function isArchivedSense(rec: unknown): boolean {
  return !!rec && typeof rec === 'object' && !isSense2(rec);
}

/** The engine version of a record, or null. Two records are comparable on one trend
 *  line ONLY when this matches (02_VALIDITY sec ד). */
export function senseEngineOf(
  rec: SenseRecord | null | undefined
): string | null {
  return (rec && rec.engine_version) || null;
}

function metricBlock(rec: SenseRecord | null | undefined, metric: SenseMetric) {
  if (!rec) return null;
  return metric === 'vision' ? rec.vision_contrast : rec.hearing_tone;
}

/** The status of one metric on a record. 'missing' when the block isn't there. */
export function metricStatus(
  rec: SenseRecord | null | undefined,
  metric: SenseMetric
): SenseStatus {
  const b = metricBlock(rec, metric);
  if (!b || !b.status) return 'missing';
  const s = b.status;
  return s === 'ok' ||
    s === 'inconclusive' ||
    s === 'ceiling' ||
    s === 'floor' ||
    s === 'skipped'
    ? (s as SenseStatus)
    : 'missing';
}

/**
 * The numeric value of one metric — ONLY when status === 'ok'. 🔴 The iron rule:
 * a non-ok metric is NEVER 0, never the average, never the previous value. It is
 * null, and null must stay null all the way to the UI. This is the single guard
 * that stops "not measured" from being drawn as a measured 0. (Handoff 02 sec C.)
 */
export function metricValue(
  rec: SenseRecord | null | undefined,
  metric: SenseMetric
): number | null {
  if (metricStatus(rec, metric) !== 'ok') return null;
  const b = metricBlock(rec, metric);
  const raw = metric === 'vision' ? (b as any)?.log_cs : (b as any)?.snr_db;
  return typeof raw === 'number' && isFinite(raw) ? raw : null;
}

export type SenseChangeState = 'stable' | 'improved' | 'declined';

export interface SenseChange {
  /** null when a delta cannot be computed (either side not ok). */
  state: SenseChangeState | null;
  /** signed current - previous, or null. For the provider report dB label. */
  delta: number | null;
}

/**
 * Change of one metric between two records. A delta exists ONLY when BOTH sides
 * are ok. Below the metric's MIN_DELTA it is "stable" (no arrow, no colour). Above
 * it, direction is decided by better() — improved / declined — never by the raw
 * sign, because the sign means opposite things for the two metrics.
 */
export function computeChange(
  metric: SenseMetric,
  current: SenseRecord | null | undefined,
  previous: SenseRecord | null | undefined
): SenseChange {
  const cur = metricValue(current, metric);
  const prev = metricValue(previous, metric);
  if (cur == null || prev == null) return { state: null, delta: null };
  const delta = cur - prev;
  if (Math.abs(delta) < SENSE_MIN_DELTA[metric]) return { state: 'stable', delta };
  return { state: SENSE_BETTER[metric](cur, prev) ? 'improved' : 'declined', delta };
}
